import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { toast } from 'react-toastify';
import './LossAdjusterPanel.css';

const LossAdjusterPanel = () => {
  const { user } = useAuth();
  const { selectedEvent, submitAdjustment, generateReport } = useApp();
  
  const [formData, setFormData] = useState({
    verifiedQuantityLoss: (selectedEvent?.quantityBefore || 0) - (selectedEvent?.quantityCurrent || 0),
    verifiedValueLoss: selectedEvent?.estimatedLoss || 0,
    damageConsistent: true,
    adjusterName: user?.name || '',
    comments: '',
    status: 'pending'
  });

  // Update form when event changes
  React.useEffect(() => {
    if (selectedEvent) {
      setFormData(prev => ({
        ...prev,
        verifiedQuantityLoss: (selectedEvent.quantityBefore || 0) - (selectedEvent.quantityCurrent || 0),
        verifiedValueLoss: selectedEvent.estimatedLoss || 0
      }));
    }
  }, [selectedEvent]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    if (!selectedEvent) {
      toast.error('Please select an event');
      return;
    }

    const result = await submitAdjustment(selectedEvent._id, {
      ...formData,
      status: 'completed'
    });

    if (result.success) {
      toast.success('Adjustment submitted successfully');
      setFormData(prev => ({ ...prev, status: 'completed' }));
    } else {
      toast.error(result.message);
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedEvent) {
      toast.error('Please select an event');
      return;
    }

    const result = await generateReport(selectedEvent._id);
    if (result.success) {
      toast.success('Report generated successfully');
    } else {
      toast.error(result.message);
    }
  };

  if (!selectedEvent) {
    return (
      <div className="loss-adjuster-panel">
        <div className="panel-header">
          <span className="panel-icon">🔍</span>
          <h3>Loss Adjustment Panel</h3>
        </div>
        <div className="no-event-message">
          <p>Select an event to begin loss adjustment</p>
        </div>
      </div>
    );
  }

  const currentAdjustment = selectedEvent.lossAdjustment;
  const isPending = !currentAdjustment || currentAdjustment.status === 'pending';

  return (
    <div className="loss-adjuster-panel">
      <div className="panel-header">
        <span className="panel-icon">🔍</span>
        <h3>Loss Adjustment Panel</h3>
        <span className={`adjustment-status ${isPending ? 'pending' : 'completed'}`}>
          {isPending ? 'Pending' : 'Completed'}
        </span>
      </div>

      <div className="event-info">
        <span>ERF: {selectedEvent.erfNumber || 'N/A'}</span>
        <span>|</span>
        <span>Commodity: {selectedEvent.commodity?.name || 'N/A'}</span>
      </div>

      <div className="adjustment-sections">
        <div className="section original-data">
          <h4>Original EO Data</h4>
          <div className="data-row">
            <span className="data-label">Quantity Loss:</span>
            <span className="data-value">
              {((selectedEvent.quantityBefore || 0) - (selectedEvent.quantityCurrent || 0)).toLocaleString()} {selectedEvent.commodity?.unit || 'units'}
            </span>
          </div>
          <div className="data-row">
            <span className="data-label">Value Loss:</span>
            <span className="data-value">R {(selectedEvent.estimatedLoss || 0).toLocaleString()}</span>
          </div>
        </div>

        <div className="section verified-data">
          <h4>Verified by Adjuster</h4>
          <div className="form-group">
            <label>Verified Quantity Loss</label>
            <input
              type="number"
              name="verifiedQuantityLoss"
              value={formData.verifiedQuantityLoss}
              onChange={handleInputChange}
              disabled={!isPending || user?.role === 'viewer'}
            />
          </div>
          <div className="form-group">
            <label>Verified Value Loss (ZAR)</label>
            <input
              type="number"
              name="verifiedValueLoss"
              value={formData.verifiedValueLoss}
              onChange={handleInputChange}
              disabled={!isPending || user?.role === 'viewer'}
            />
          </div>
        </div>
      </div>

      <div className="form-group checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="damageConsistent"
            checked={formData.damageConsistent}
            onChange={handleInputChange}
            disabled={!isPending || user?.role === 'viewer'}
          />
          <span>Damage Consistent with EO Data?</span>
          <span className={`consistency-badge ${formData.damageConsistent ? 'yes' : 'no'}`}>
            {formData.damageConsistent ? 'Yes' : 'No'}
          </span>
        </label>
      </div>

      <div className="form-group">
        <label>Loss Adjuster Name</label>
        <input
          type="text"
          name="adjusterName"
          value={formData.adjusterName}
          onChange={handleInputChange}
          placeholder="Enter your full name"
          disabled={!isPending || user?.role === 'viewer'}
        />
      </div>

      <div className="form-group">
        <label>Assessment Comments</label>
        <textarea
          name="comments"
          value={formData.comments}
          onChange={handleInputChange}
          placeholder="Enter detailed assessment notes, methodology, and observations..."
          rows={4}
          disabled={!isPending || user?.role === 'viewer'}
        />
      </div>

      <div className="form-group">
        <label>Adjustment Status</label>
        <select
          name="status"
          value={formData.status}
          onChange={handleInputChange}
          disabled={user?.role === 'viewer'}
        >
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div className="panel-actions">
        {(user?.role === 'administrator' || user?.role === 'loss_adjuster') && (
          <button className="btn btn-primary" onClick={handleSubmit}>
            Submit Review
          </button>
        )}
        <button className="btn btn-secondary" onClick={handleGenerateReport}>
          Generate Report PDF
        </button>
      </div>

      <div className="panel-footer">
        <span className="footer-note">
          🤖 Loss validated and adjusted using AI-powered satellite analysis by Google Gemini.
        </span>
      </div>
    </div>
  );
};

export default LossAdjusterPanel;
